import { Stack } from '@mui/material';
import { useMemo } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { SideDrawer } from '@/modules/common/SideDrawer/SideDrawer';
import { INSURANCE_POLICY_ENTITY_TYPES } from '@/modules/entities/entities.constants';
import { makeUpdatePolicyInputs } from '@/modules/entities/EntitySubforms/utils/shared/policies.utils';
import { useUpdateEntityMutation } from '@/modules/entities/graphql/UpdateEntity.generated';
import { InsurancePolicies_InsurancePolicyFragment } from '@/modules/entities/InsurancePolicyDetailsSubform/graphql/InsurancePolicies.generated';
import { getInsurancePolicyIntegrationPrefilledFields } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.integration';
import {
  Fields,
  INSURANCE_POLICIES_DETAILS_SUBFORM,
  InsurancePolicyDetails,
} from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import { InsurancePolicyForm } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.UI';
import { mapInsurancePolicy } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.utils';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { getEntitySubtypeUpdateFieldForPolicy } from '@/modules/estate/estateHoldings/estateHoldings.utils';
import { Operations as EstatePageOperations } from '@/modules/estate/graphql/EstatePageData.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AugmentedUpdateEntityInput, EntityKind } from '@/types/schema';

export interface EditPolicySideDrawerProps {
  /**
   * Whether the drawer is open
   */
  isOpen: boolean;
  /**
   * Function to call when closing the drawer
   */
  onClose: () => void;
  /**
   * Called after the policy is saved successfully, before the drawer closes.
   * Lets a host (e.g. a presentation slide driving its own query) refetch so
   * its view reflects the edit. Optional — a pure cancel never fires it.
   */
  onSaved?: () => void;
  /**
   * The entity holding the insurance policy (minimal fields needed)
   */
  entity: {
    id: string;
    kind: EntityKind;
    subtype: {
      id: string;
    };
  };
  /**
   * The insurance policy being edited
   */
  policy: InsurancePolicies_InsurancePolicyFragment;
}

/**
 * Inner component that uses the form context
 */
function EditPolicySideDrawerInner({
  isOpen,
  onClose,
  onSaved,
  entity,
  initialIntegrationPrefilledFields,
}: Omit<EditPolicySideDrawerProps, 'policy'> & {
  initialIntegrationPrefilledFields: Set<keyof InsurancePolicyDetails>;
}) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { formRef, handleSave } = useFormSaveHandler();
  const { householdId } = useHouseholdDetailsContext();
  const { handleSubmit, reset } = useFormContext<Fields>();

  const [updateEntityMutation, { loading }] = useUpdateEntityMutation({
    refetchQueries: [EstatePageOperations.Query.EstatePageData],
    onError: (error) => {
      showFeedback(FeedbackMessages.formSaveError);
      reportError(`Could not update insurance policy`, error);
    },
    onCompleted: () => {
      showFeedback('Insurance policy updated successfully', {
        variant: 'success',
      });
      onSaved?.();
      handleClose();
    },
  });

  const onSubmit = handleSubmit(async (formData) => {
    try {
      const subtypeId = entity.subtype.id;
      const policies: InsurancePolicyDetails[] =
        formData[INSURANCE_POLICIES_DETAILS_SUBFORM]?.policies || [];

      if (policies.length === 0) {
        showFeedback('No policy data to update');
        return;
      }

      // Create the update input using the existing utility
      const { updatePolicies } = makeUpdatePolicyInputs(
        formData[INSURANCE_POLICIES_DETAILS_SUBFORM],
        householdId!
      );

      const entityType = getEntityTypeFromEntityKind(entity.kind);
      const entitySubtypeUpdateField =
        getEntitySubtypeUpdateFieldForPolicy(entityType);
      if (!entitySubtypeUpdateField) {
        throw new Error(
          `Unsupported entity type for insurance policies: ${entityType}. Supported types: ${INSURANCE_POLICY_ENTITY_TYPES.join(', ')}`
        );
      }

      const updateEntityInput: AugmentedUpdateEntityInput = {
        id: entity.id,
        update: {},
        [entitySubtypeUpdateField]: {
          id: subtypeId,
          update: {},
          updatePolicies,
        },
      };

      await updateEntityMutation({
        variables: { input: updateEntityInput },
      });
    } catch (error) {
      reportError('Failed to update insurance policy', error as Error);
      showFeedback('Failed to update insurance policy. Please try again.');
    }
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={handleClose}
      heading="Edit insurance policy"
      data-testid="edit-policy-side-drawer"
      body={
        <Stack gap={2} mt={2} width="100%" flex={1}>
          <form ref={formRef} onSubmit={onSubmit} noValidate>
            <InsurancePolicyForm
              policyIndex={0}
              householdId={householdId!}
              entityId={entity.id}
              remove={() => {}}
              additionalItemRendererProps={{ hideDeleteButton: true }}
              isHypothetical={false}
              isCreateHypothetical={false}
              variant="default"
              internalCardVariantOverride="filled-outline"
              initialIntegrationPrefilledFields={
                initialIntegrationPrefilledFields
              }
              hideIntegrationSelect
            />
          </form>
        </Stack>
      }
      footer={
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            loading={loading}
            disabled={loading}
          >
            Save changes
          </Button>
        </Stack>
      }
    />
  );
}

/**
 * Side drawer for editing insurance policies.
 * Initializes the form with the actual policy data so fields are
 * correctly prepopulated on mount (including integration-linked policies).
 */
export function EditPolicySideDrawer(props: EditPolicySideDrawerProps) {
  const mappedPolicy = useMemo(
    () => mapInsurancePolicy(props.policy),
    [props.policy]
  );
  const initialIntegrationPrefilledFields = useMemo(
    () => getInsurancePolicyIntegrationPrefilledFields(mappedPolicy),
    [mappedPolicy]
  );

  const formMethods = useForm<Fields>({
    defaultValues: {
      [INSURANCE_POLICIES_DETAILS_SUBFORM]: {
        policies: [mappedPolicy],
        initialPolicyIDs: [props.policy.id],
        crummeyWithdrawalPeriodDays: null,
      },
    },
  });

  return (
    <FormProvider {...formMethods}>
      <EditPolicySideDrawerInner
        {...props}
        initialIntegrationPrefilledFields={initialIntegrationPrefilledFields}
      />
    </FormProvider>
  );
}
